import { Box, Divider, IconButton } from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';

import { StyledTextField } from '../../../../../../components/UI/Typography';
import { Constants } from '../../../../../../constants/hiveNotices';
import { IconCloseMS, IconDeleteMS } from '../../../../../../constants/icons';
import { DocumentCard } from '../../components/DocumentCard';
import { DocumentUploadBox } from '../../components/DocumentUploadBox';
import { CancelButton, CreateNoticeButton } from '../styles';
import { DocumentEntry } from '../types';
import {
  DrawerActions,
  DrawerHeader,
  DrawerSubtitle,
  DrawerTitle,
  DrawerTitleRow,
  StyledDrawer,
  DeleteDoc,
} from './styles';

interface DocumentDrawerProps {
  open: boolean;
  onClose: () => void;
  onAddDocument: (doc: DocumentEntry) => void;
  editDoc?: DocumentEntry;
  onDeleteDoc?: () => void;
  existingNames: string[];
}

export function DocumentDrawer({
  open,
  onClose,
  onAddDocument,
  editDoc,
  onDeleteDoc,
  existingNames,
}: DocumentDrawerProps): JSX.Element {
  const [docName, setDocName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | undefined>();

  useEffect(() => {
    if (open && editDoc) {
      setDocName(editDoc.name);
      setSelectedFile(editDoc.file);
    } else if (!open) {
      setDocName('');
      setSelectedFile(undefined);
    }
  }, [open, editDoc]);

  const normalize = (s: string): string =>
    s.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

  const isDuplicateName =
    !!docName && existingNames.some((n) => normalize(docName) === normalize(n));

  const handleClose = (): void => {
    setDocName('');
    setSelectedFile(undefined);
    onClose();
  };

  const handleAdd = (): void => {
    if (!docName || !selectedFile || isDuplicateName) return;
    onAddDocument({ name: docName, file: selectedFile });
    handleClose();
  };

  const isValid = !!docName && !!selectedFile && !isDuplicateName;

  return (
    <StyledDrawer anchor="right" open={open} onClose={handleClose}>
      <DrawerHeader>
        <IconButton onClick={handleClose}>{IconCloseMS}</IconButton>
      </DrawerHeader>
      <Divider />
      <DrawerTitleRow>
        <DrawerTitle>
          {editDoc ? Constants.editDoc : Constants.addDocumentToAccept}
        </DrawerTitle>
        {editDoc && onDeleteDoc && (
          <DeleteDoc onClick={onDeleteDoc}>
            {IconDeleteMS} {Constants.removeFromNotice}
          </DeleteDoc>
        )}
      </DrawerTitleRow>
      <DrawerSubtitle>{Constants.acceptDocObs}</DrawerSubtitle>
      <StyledTextField
        label={Constants.docName}
        variant="outlined"
        value={docName}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setDocName(e.target.value)
        }
        required
        error={isDuplicateName}
        helperText={isDuplicateName ? Constants.duplicateDocName : undefined}
        inputProps={{ maxLength: 100 }}
      />
      <Box mt={3}>
        {selectedFile ? (
          <DocumentCard
            name={docName || selectedFile.name}
            file={selectedFile}
            onDelete={() => setSelectedFile(undefined)}
            onDrawer
          />
        ) : (
          <DocumentUploadBox
            onFileSelect={setSelectedFile}
            inputId="drawerDocumentUpload"
            fullWidth
          />
        )}
      </Box>
      <DrawerActions>
        <CancelButton onClick={handleClose}>{Constants.cancel}</CancelButton>
        <CreateNoticeButton
          onClick={handleAdd}
          disabled={!isValid}
          style={{
            opacity: isValid ? 1 : 0.5,
            cursor: isValid ? 'pointer' : 'not-allowed',
          }}
        >
          {editDoc ? Constants.saveDoc : Constants.addDoc}
        </CreateNoticeButton>
      </DrawerActions>
    </StyledDrawer>
  );
}
